// MediaDisplay - Read-only display of task media attachments
// Used in TaskDataEditModal to show existing photos, videos, documents

import {
  Box,
  VStack,
  Text,
  Link,
  Image,
  SimpleGrid,
  AspectRatio,
  Icon,
  HStack,
} from '@chakra-ui/react';
import { FiImage, FiVideo, FiFile, FiExternalLink } from 'react-icons/fi';

interface MediaDisplayProps {
  photos?: string[];
  videos?: string[];
  documents?: string[];
  media?: string[];
}

/**
 * Read-only display component for task media attachments
 * Shows thumbnails for photos and clickable links for videos/documents
 */
export function MediaDisplay({ photos, videos, documents, media }: MediaDisplayProps) {
  const hasPhotos = photos && photos.length > 0;
  const hasVideos = videos && videos.length > 0;
  const hasDocuments = documents && documents.length > 0;
  const hasMedia = media && media.length > 0;

  if (!hasPhotos && !hasVideos && !hasDocuments && !hasMedia) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={3}>
      {/* Photos */}
      {hasPhotos && (
        <Box>
          <HStack spacing={1} mb={2}>
            <Icon as={FiImage} color="text-muted" />
            <Text fontSize="sm" fontWeight="medium" color="text-secondary">
              Photos ({photos!.length})
            </Text>
          </HStack>
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
            {photos!.map((url, index) => (
              <Link
                key={index}
                href={url}
                isExternal
                _hover={{ opacity: 0.8, transform: 'scale(1.02)' }}
                transition="all 0.2s"
              >
                <AspectRatio ratio={4 / 3}>
                  <Image
                    src={url}
                    alt={`Photo ${index + 1}`}
                    objectFit="cover"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border-default"
                  />
                </AspectRatio>
              </Link>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Videos */}
      {hasVideos && (
        <Box>
          <HStack spacing={1} mb={2}>
            <Icon as={FiVideo} color="text-muted" />
            <Text fontSize="sm" fontWeight="medium" color="text-secondary">
              Videos ({videos!.length})
            </Text>
          </HStack>
          <VStack align="stretch" spacing={2}>
            {videos!.map((url, index) => (
              <Link
                key={index}
                href={url}
                isExternal
                display="flex"
                alignItems="center"
                p={3}
                borderWidth="1px"
                borderColor="border-default"
                borderRadius="md"
                bg="bg-surface"
                _hover={{ bg: 'interactive-hover', borderColor: 'border-hover' }}
                transition="all 0.2s"
              >
                <Icon as={FiVideo} mr={2} color="brand.500" />
                <Text flex="1" fontSize="sm" color="text-primary">
                  Video {index + 1}
                </Text>
                <Icon as={FiExternalLink} color="text-muted" />
              </Link>
            ))}
          </VStack>
        </Box>
      )}

      {/* Documents */}
      {hasDocuments && (
        <Box>
          <HStack spacing={1} mb={2}>
            <Icon as={FiFile} color="text-muted" />
            <Text fontSize="sm" fontWeight="medium" color="text-secondary">
              Documents ({documents!.length})
            </Text>
          </HStack>
          <VStack align="stretch" spacing={2}>
            {documents!.map((url, index) => (
              <Link
                key={index}
                href={url}
                isExternal
                display="flex"
                alignItems="center"
                p={3}
                borderWidth="1px"
                borderColor="border-default"
                borderRadius="md"
                bg="bg-surface"
                _hover={{ bg: 'interactive-hover', borderColor: 'border-hover' }}
                transition="all 0.2s"
              >
                <Icon as={FiFile} mr={2} color="brand.500" />
                <Text flex="1" fontSize="sm" color="text-primary">
                  Document {index + 1}
                </Text>
                <Icon as={FiExternalLink} color="text-muted" />
              </Link>
            ))}
          </VStack>
        </Box>
      )}

      {/* Generic Media */}
      {hasMedia && (
        <Box>
          <HStack spacing={1} mb={2}>
            <Icon as={FiFile} color="text-muted" />
            <Text fontSize="sm" fontWeight="medium" color="text-secondary">
              Media Files ({media!.length})
            </Text>
          </HStack>
          <VStack align="stretch" spacing={2}>
            {media!.map((url, index) => (
              <Link
                key={index}
                href={url}
                isExternal
                display="flex"
                alignItems="center"
                p={3}
                borderWidth="1px"
                borderColor="border-default"
                borderRadius="md"
                bg="bg-surface"
                _hover={{ bg: 'interactive-hover', borderColor: 'border-hover' }}
                transition="all 0.2s"
              >
                <Icon as={FiFile} mr={2} color="brand.500" />
                <Text flex="1" fontSize="sm" color="text-primary">
                  Media {index + 1}
                </Text>
                <Icon as={FiExternalLink} color="text-muted" />
              </Link>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
}
